import stringify from 'json-stable-stringify';

import {requestFetch} from 'models/Autobatcher';
import {ResolutionQuery} from 'notebook/tables/excess/Resolver';
/* eslint-disable-next-line import/order */
import {getStore} from 'redux-stores/Store';

// Don't touch this! The app will crash on load if this block of imports is moved
import {
  CustomResolutionQuery,
  CustomResolutionQueryType,
  isCustomResolutionQuery,
  StringResolutionQuery,
  StringResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  bulkResolveCustomResolutionQueries,
  bulkResolveStringsToValues,
  clearCache,
  clearResolutionResultsByTypeAndId,
} from 'notebook/tables/string-resolution/StringResolutionReduxActions';

/**
 * Model manager for ELN string resolution.
 *
 * modelKey is the json-stable-stringified form of the resolutionQuery.
 */
export default class StringResolutionManager {
  static getOrFetch(resolutionQuery: ResolutionQuery): StringResolutionResult | null {
    const modelKey = stringify(resolutionQuery);
    const resolutionResult = this.get(modelKey);
    if (resolutionResult) {
      return resolutionResult;
    } else {
      requestFetch(this, modelKey);
      return null;
    }
  }

  static get(modelKey: string): StringResolutionResult | null {
    const elnState = getStore().getState().models.eln;
    const resolutionResult = elnState.resolutionResultsByResolutionQuery[modelKey];
    return resolutionResult ?? null;
  }

  static async fetchMulti(modelKeys: Array<string>): Promise<void> {
    const stringResolutionQueries: Array<StringResolutionQuery> = [];
    const customResolutionQueries: Array<CustomResolutionQuery> = [];
    for (const modelKey of modelKeys) {
      const resolutionQuery: ResolutionQuery = JSON.parse(modelKey);
      if (isCustomResolutionQuery(resolutionQuery)) {
        customResolutionQueries.push(resolutionQuery);
      } else {
        stringResolutionQueries.push(resolutionQuery);
      }
    }
    await Promise.all([
      bulkResolveStringsToValues(stringResolutionQueries),
      bulkResolveCustomResolutionQueries(customResolutionQueries),
    ]);
  }

  static isKeyLoadedOrLoading(modelKey: string): boolean {
    const elnState = getStore().getState().models.eln;
    return Boolean(
      elnState.stringResolutionLoadingState[modelKey] || elnState.resolutionResultsByResolutionQuery[modelKey]
    );
  }

  static clearStringResolutionReduxCache(): void {
    clearCache();
  }

  static clearStringResolutionReduxCacheByTypeAndId(
    customResolutionQueryType: CustomResolutionQueryType,
    ids: Array<string>
  ): void {
    clearResolutionResultsByTypeAndId(customResolutionQueryType, ids);
  }
}
